import React, { useRef, useEffect, useCallback } from 'react';
import isFunction from 'lodash/isFunction';

import { findFirstTabbableElement, findLastTabbableElement } from '@Utils/DOM';

const instances = [];

function isInPage(node) {
  return node === document.body ? false : document.body.contains(node);
}

export function KeepFocusWrapper({ children, focusOnMount = false, needKeepFocus = false, containerProps = {}, ...props }) {
  const ref = useRef(null);
  const lastFocusRef = useRef(null);
  const originalFocusRef = useRef(null);
  const instanceRef = useRef(null);
  const { returnFocusElement } = props;

  const setFocusInsideComponentDomNode = useCallback((e) => {
    const { current } = ref;

    if (!current) return;

    const isFocusInside = current.contains(e.target);

    if (isFocusInside) {
      lastFocusRef.current = e.target;
      return;
    }

    try {
      const first = findFirstTabbableElement(current);
      const last = findLastTabbableElement(current);

      if (e.target === document) {
        // when window gets focus - return to previous focus
        if (lastFocusRef.current && isFunction(lastFocusRef.current?.focus)) {
          lastFocusRef.current.focus();
        }
      } else if (lastFocusRef.current === first) {
        // when on first tabbable element - go to last
        last.focus();
      } else if (lastFocusRef.current === last) {
        // when on last tabbable element - go to first
        first.focus();
      } else if (first) {
        // Focus the first tabbable element if available
        first.focus();
      } else {
        current.focus();
      }
    } catch (error) {
      Logger.error('Error in KeepFocus::setFocusInsideComponentDomNode:', error);
    }
  }, []);

  const handleFocus = useCallback(
    (e) => {
      if (document.querySelector('.c-els-modal')?.contains(e.target)) {
        // Skip if the focus is within Leyden modal window, e.g. structure modal in create alert dialog
        return;
      }

      if (instances[instances.length - 1] !== instanceRef.current) {
        // Skip focus if another custom dialog is on top of the current one
        return;
      }

      if (!needKeepFocus) {
        return;
      }

      setFocusInsideComponentDomNode(e);
    },
    [needKeepFocus, setFocusInsideComponentDomNode]
  );

  useEffect(() => {
    instanceRef.current = { ref, lastFocusRef, originalFocusRef };
    instances.push(instanceRef.current);

    document.addEventListener('focus', handleFocus, true);

    if (needKeepFocus) {
      originalFocusRef.current = document.activeElement;
    }

    if (focusOnMount && ref.current) {
      ref.current.focus();
    }

    return () => {
      document.removeEventListener('focus', handleFocus, true);
      const index = instances.findIndex((instance) => instance === instanceRef.current);
      const focusTarget = returnFocusElement || originalFocusRef.current;

      if (focusTarget && isFunction(focusTarget?.focus) && isInPage(focusTarget)) {
        focusTarget.focus();
      }

      if (index !== -1) {
        instances.splice(index, 1);
      }
    };
  }, [focusOnMount, needKeepFocus, handleFocus, returnFocusElement]);

  if (isFunction(children)) {
    return (
      <>
        <div {...containerProps} data-testid="keep-focus-wrapper" tabIndex="-1" ref={ref}>
          {children(props)}
        </div>
        {/*
          Need at least one focusable element in DOM after the focus trap, otherwise focus event handler is not called, and focus moves to browser toolbar.
          This is only for our old custom dialogs, Leyden modals have their own focus trap and there it is not an issue.
        */}
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
        <div tabIndex="0" />
      </>
    );
  }

  return (
    <>
      <div {...containerProps} data-testid="keep-focus-wrapper" tabIndex="-1" ref={ref}>
        {children}
      </div>
      {/*
        Need at least one focusable element in DOM after the focus trap, otherwise focus event handler is not called, and focus moves to browser toolbar.
        This is only for our old custom dialogs, Leyden modals have their own focus trap and there it is not an issue.
      */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <div tabIndex="0" />
    </>
  );
}

export function KeepFocusHoc(config = {}) {
  const { needKeepFocus, focusOnMount } = config;

  return function KeepFocusProvider(Component) {
    return function KeepFocusWrapperHoc(props) {
      const { children, ...otherProps } = props;
      const nextNeedKeepFocus = isFunction(needKeepFocus) ? needKeepFocus(props) : Boolean(needKeepFocus);

      return (
        <KeepFocusWrapper {...otherProps} {...config} needKeepFocus={nextNeedKeepFocus} focusOnMount={focusOnMount}>
          <Component {...otherProps}>{children}</Component>
        </KeepFocusWrapper>
      );
    };
  };
}
